import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { SCHEMAS } from './schemas';
import { adminArtists, adminArtworks, adminExhibitions, adminJournal, adminVideos } from '@/lib/admin';
import { getSiteSettings } from '@/lib/api';
import { FRENCH_PUBLIC_ENABLED } from '@/lib/i18n';

const API = {
    artists: adminArtists,
    artworks: adminArtworks,
    exhibitions: adminExhibitions,
    journal: adminJournal,
    videos: adminVideos,
};

/* Minimum required French fields per entity (mirrors backend REQUIRED_FR
   in /app/backend/i18n.py). A record is publishable in French when all
   listed keys are non-empty. Kept in sync manually — small enough to be
   worth avoiding a bootstrap fetch.

   NB: These arrays are only consulted when `FRENCH_PUBLIC_ENABLED` is true.
   In the current English-only launch mode `frStatus()` short-circuits to
   `null`, so no FR-status pill renders and this map is inert. */
const REQUIRED_FR = {
    artists: ['statement_fr', 'biography_fr'],
    artworks: ['description_fr'],
    exhibitions: ['subtitle_fr', 'description_fr'],
    journal: ['title_fr', 'excerpt_fr', 'body_fr'],
    videos: ['title_fr', 'description_fr'],
};

function frStatus(entity, r) {
    // English-only launch mode: never render the FR READY/FR MISSING pill on
    // list rows. When French returns, flip FRENCH_PUBLIC_ENABLED to `true`
    // and the pill logic below reactivates automatically.
    if (!FRENCH_PUBLIC_ENABLED) return null;
    const req = REQUIRED_FR[entity] || [];
    if (!req.length) return null;
    const complete = req.every((k) => r[k] && String(r[k]).trim());
    return complete
        ? { label: 'FR READY', tone: 'ready' }
        : { label: 'FR MISSING', tone: 'missing' };
}

function CollectionList({ entity }) {
    const schema = SCHEMAS[entity];
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);

    const reload = () => { setBusy(true); API[entity].list().then(setItems).finally(() => setBusy(false)); };
    useEffect(reload, [entity]);

    const remove = async (slug) => {
        if (!window.confirm(`Delete this ${schema.label.toLowerCase()}?`)) return;
        await API[entity].remove(slug);
        reload();
    };

    return (
        <div data-testid={`admin-${entity}-list`} className="py-10">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                <div>
                    <span className="section-no">§ {schema.label}s</span>
                    <h1 className="serif text-5xl md:text-7xl mt-3 leading-[0.95]">{schema.label}s</h1>
                </div>
                <Link to={`/admin/${entity}/new`} data-testid={`admin-${entity}-new`} className="btn-primary">+ New {schema.label.toLowerCase()}</Link>
            </div>
            {busy && <p className="literary">Loading…</p>}
            {!busy && items.length === 0 && <p className="literary">Nothing here yet.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mt-8">
                {items.map((r) => (
                    <Link key={r.slug} to={`/admin/${entity}/${r.slug}`} data-testid={`admin-${entity}-row-${r.slug}`} className="group">
                        <div className="img-wrap aspect-[4/5]">
                            {schema.primaryImage(r)
                                ? <img src={schema.primaryImage(r)} alt={schema.primaryTitle(r)} />
                                : <div className="w-full h-full flex items-center justify-center caption" style={{ background: 'var(--paper-2)' }}>No image</div>}
                        </div>
                        <div className="mt-4">
                            <h3 className="serif text-2xl leading-[1.1]">{schema.primaryTitle(r)}</h3>
                            <div className="caption mt-2">{schema.secondary(r)}</div>
                            <div className="mt-3 flex justify-between items-center caption">
                                <span className="label" style={{ color: 'var(--ink-3)' }}>/{r.slug}</span>
                                {(() => {
                                    const fs = frStatus(entity, r);
                                    if (!fs) return null;
                                    return (
                                        <span
                                            data-testid={`admin-${entity}-fr-${r.slug}`}
                                            className="label"
                                            title={fs.tone === 'ready'
                                                ? 'All required French fields present — this record will appear on French pages when French is publicly enabled.'
                                                : 'One or more required French fields are empty — this record will remain hidden on French pages.'}
                                            style={{
                                                fontSize: 10,
                                                letterSpacing: '0.08em',
                                                color: 'var(--ink-3)',
                                                opacity: fs.tone === 'ready' ? 0.85 : 0.5,
                                            }}
                                        >{fs.label}</span>
                                    );
                                })()}
                                <button onClick={(e) => { e.preventDefault(); remove(r.slug); }} className="label hover:text-[var(--clay-deep)]" data-testid={`admin-${entity}-delete-${r.slug}`}>Delete</button>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function CollectionEditor({ entity }) {
    const { slug } = useParams();
    const nav = useNavigate();
    const schema = SCHEMAS[entity];
    const isNew = !slug || slug === 'new';
    const [form, setForm] = useState(schema.defaults());
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ctx, setCtx] = useState({});

    useEffect(() => {
        if (!isNew) {
            API[entity].list().then((all) => {
                const existing = all.find((x) => x.slug === slug);
                if (existing) setForm({ ...schema.defaults(), ...existing });
            });
        }
        // Always load artists for artwork editor context
        if (entity === 'artworks') {
            adminArtists.list().then((arts) => setCtx({ artists: arts }));
        }
        // Videos editor needs artists + exhibitions for the optional pickers,
        // and the CMS-editable list of categories from site settings.
        if (entity === 'videos') {
            Promise.all([adminArtists.list(), adminExhibitions.list(), getSiteSettings()])
                .then(([arts, exs, settings]) => setCtx({
                    artists: arts,
                    exhibitions: exs,
                    categories: settings?.film_page?.categories || null,
                }));
        }
    }, [entity, slug, isNew]);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            if (isNew) {
                await API[entity].create(form);
            } else {
                await API[entity].update(slug, form);
            }
            nav(`/admin/${entity}`, { replace: true });
        } catch (e2) {
            const d = e2.response?.data?.detail;
            setErr(typeof d === 'string' ? d : 'Save failed');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={save} className="py-10" data-testid={`admin-${entity}-editor`}>
            <div className="flex items-center justify-between mb-12">
                <div>
                    <span className="section-no">§ {isNew ? 'New' : 'Edit'} {schema.label.toLowerCase()}</span>
                    <h1 className="serif text-4xl md:text-6xl mt-3 leading-[0.95]">{isNew ? `New ${schema.label.toLowerCase()}` : (form.title || form.name || schema.label)}</h1>
                </div>
                <Link to={`/admin/${entity}`} className="label" style={{ color: 'var(--ink-3)' }}>← Back</Link>
            </div>

            <div className="flex flex-col gap-10 max-w-3xl">
                {schema.render(form, setForm, ctx)}
            </div>

            {err && <p data-testid="editor-error" className="caption mt-6" style={{ color: 'var(--clay-deep)' }}>{err}</p>}

            <div className="mt-12 flex items-center gap-6">
                <button data-testid="editor-save" type="submit" disabled={busy} className="btn-primary">{busy ? 'Saving…' : isNew ? 'Publish' : 'Save changes'}</button>
                <Link to={`/admin/${entity}`} className="label" style={{ color: 'var(--ink-3)' }}>Cancel</Link>
            </div>
        </form>
    );
}

export { CollectionList, CollectionEditor };
